import { Epic } from 'redux-observable'
import { filter, map, pluck, mergeMap, switchMap } from 'rxjs/operators'
import { from } from 'rxjs'
import { tag } from 'rxjs-spy/operators/tag'
import { isOfType } from 'typesafe-actions'
import { EpicDependencies, RootState } from 'src/state/types'
import { ENDPOINTS } from './constants'
import { DocumentsEvent, events } from './state'
import { IDocument, IDocumentResponse } from './types'

// Transform API response to internal format
const transformDocumentResponse = (doc: IDocumentResponse): IDocument => ({
  attached_at: doc.attachedAt,
  id: doc.documentId,
  type: doc.filetype,
  name: doc.filename,
  updated_at: doc.updatedAt,
})

export const apiFetchDocuments: Epic<DocumentsEvent, DocumentsEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  dependencies,
) => {
  return action$.pipe(
    filter(isOfType(events.documents_request.type)),
    mergeMap(action =>
      dependencies
        .get(ENDPOINTS.FETCH_DOCUMENTS, state$, {
          className: action.payload.className,
          classId: action.payload.classId,
        })
        .pipe(
          pluck('response'),
          map(response => {
            const documents = response.documents?.map(transformDocumentResponse) || []
            return events.documents_success({
              className: action.payload.className,
              classId: action.payload.classId,
              documents,
            })
          }),
          dependencies.catchRestError(action),
        ),
    ),
    tag('documents/epics/apiFetchDocuments'),
  )
}

export const apiFetchDocumentTags: Epic<DocumentsEvent, DocumentsEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  dependencies,
) => {
  return action$.pipe(
    filter(isOfType(events.documentTags_request.type)),
    mergeMap(action =>
      dependencies.get(ENDPOINTS.FETCH_DOCUMENT_TAGS, state$, { documentId: action.payload.documentId }).pipe(
        pluck('response'),
        map(response =>
          events.documentTags_success({
            documentId: action.payload.documentId,
            tags: response.tags || {},
          }),
        ),
        dependencies.catchRestError(action),
      ),
    ),
    tag('documents/epics/apiFetchDocumentTags'),
  )
}

export const apiDownloadDocument: Epic<DocumentsEvent, DocumentsEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  dependencies,
) => {
  return action$.pipe(
    filter(isOfType(events.downloadDocument_request.type)),
    mergeMap(action =>
      dependencies
        .get(ENDPOINTS.DOWNLOAD_DOCUMENT, state$, {
          documentId: action.payload.documentId,
        })
        .pipe(
          pluck('response'),
          switchMap(response => {
            const { url, filename, filetype } = response
            return from(
              fetch(url)
                .then(res => res.blob())
                .then(blob => {
                  const downloadUrl = window.URL.createObjectURL(new Blob([blob], { type: filetype }))
                  const anchor = window.document.createElement('a')
                  anchor.href = downloadUrl
                  anchor.download = filename
                  window.document.body.appendChild(anchor)
                  anchor.click()
                  window.document.body.removeChild(anchor)
                  window.URL.revokeObjectURL(downloadUrl)
                }),
            ).pipe(
              map(() =>
                events.downloadDocument_success({
                  documentId: action.payload.documentId,
                  downloadUrl: response.url,
                }),
              ),
            )
          }),
          dependencies.catchRestError(action),
        ),
    ),
    tag('documents/epics/apiDownloadDocument'),
  )
}

export const apiRemoveDocument: Epic<DocumentsEvent, DocumentsEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  dependencies,
) => {
  return action$.pipe(
    filter(isOfType(events.removeDocument_request.type)),
    mergeMap(action =>
      dependencies
        .remove(ENDPOINTS.REMOVE_DOCUMENT, state$, {
          documentId: action.payload.documentId,
        })
        .pipe(
          pluck('response'),
          map(() =>
            events.removeDocument_success({
              documentId: action.payload.documentId,
              className: action.payload.className,
              classId: action.payload.classId,
            }),
          ),
          dependencies.catchRestError(action),
        ),
    ),
    tag('documents/epics/apiRemoveDocument'),
  )
}

export const apiRenameDocument: Epic<DocumentsEvent, DocumentsEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  dependencies,
) => {
  return action$.pipe(
    filter(isOfType(events.renameDocument_request.type)),
    mergeMap(action =>
      dependencies
        .put(
          ENDPOINTS.RENAME_DOCUMENT,
          state$,
          { name: action.payload.name },
          {
            documentId: action.payload.documentId,
          },
        )
        .pipe(
          pluck('response'),
          map(() =>
            events.renameDocument_success({
              documentId: action.payload.documentId,
              name: action.payload.name,
              className: action.payload.className,
              classId: action.payload.classId,
            }),
          ),
          dependencies.catchRestError(action),
        ),
    ),
    tag('documents/epics/apiRenameDocument'),
  )
}

export const apiUpdateDocumentTags: Epic<DocumentsEvent, DocumentsEvent, RootState, EpicDependencies> = (
  action$,
  state$,
  dependencies,
) => {
  return action$.pipe(
    filter(isOfType(events.updateDocumentTags_request.type)),
    mergeMap(action =>
      dependencies
        .put(
          ENDPOINTS.UPDATE_DOCUMENT_TAGS,
          state$,
          { tags: action.payload.tags },
          {
            documentId: action.payload.documentId,
          },
        )
        .pipe(
          pluck('response'),
          map(() =>
            events.updateDocumentTags_success({
              documentId: action.payload.documentId,
              tags: action.payload.tags,
            }),
          ),
          dependencies.catchRestError(action),
        ),
    ),
    tag('documents/epics/apiUpdateDocumentTags'),
  )
}
